<template>
    <div>
        <v-data-table-server
            :loading="isLoading"
            :headers="headers"
            data-test="user-list-table"
            :items="serverItems"
            :search="search"
            class="mb-0"
            density="compact"
            :page="page"
            :items-length="totalItems"
            :items-per-page="itemsPerPage"
            :items-per-page-options="itemsPerPageOptions"
            :items-per-page-text="itemsPerPageText"
            :no-data-text="noItemsText"
            :loading-text="loadingText"
            mobile-breakpoint="sm"
            disable-sort
            hide-default-footer
            @update:options="loadUsers"
        >
            <template v-slot:loading>
                <v-skeleton-loader type="table-row@6"></v-skeleton-loader>
            </template>
            <template v-slot:top>
                <div
                    class="d-flex flex-wrap ga-8 justify-space-between align-center border-b-sm pb-4"
                >
                    <div class="d-flex flex-column flex-nowrap ga-2 justify-space-between w-50">
                        <v-text-field
                            class="w-100"
                            v-model="searchName"
                            label="Suche nach Vorname, Nachname oder Email"
                            placeholder="Mustermann"
                            variant="outlined"
                            density="compact"
                            hide-details="auto"
                        />
                        <div class="d-flex flex-row flex-wrap g-2 justify-space-between">
                            <v-text-field
                                class="py-3 mr-2"
                                v-model="searchRollenname"
                                label="Suche nach Rolle"
                                placeholder="Lehrende(r)"
                                variant="outlined"
                                density="compact"
                                hide-details="auto"
                                max-width="300"
                            />
                            <v-btn-toggle
                                class="mt-3"
                                v-model="searchsIsEnabled"
                                color="primary"
                                variant="tonal"
                                density="compact"
                            >
                                <v-btn :value="true">Aktiv</v-btn>
                                <v-btn :value="false">Inaktiv</v-btn>
                            </v-btn-toggle>
                        </div>
                        <common-studiengang-select
                            @studiengang-selected="searchStudiengangId = $event"
                        />
                    </div>
                    <v-data-table-footer
                        class="border-box border-black"
                        :items-per-page-text="itemsPerPageText"
                        :itemsPerPageOptions="itemsPerPageOptions"
                        show-current-page
                    />
                </div>
            </template>
            <template #item.rollen="{ item }">
                <div class="d-flex flex-row flex-nowrap justify-content-between">
                    <div class="flex-column">
                        <div v-for="(rolle, index) in item.rollen" :key="index" class="flex-item">
                            <span class="font-weight-bold"> {{ index }}</span> für
                            <span v-if="rolle.isForAll"> alle Studiengänge </span>
                            <div v-else>
                                <template v-if="isLoadingModules">
                                    <v-skeleton-loader v-if="isLoadingModules" type="text" :key="`loader-${modulId}`" />
                                </template>
                                <template v-else>
                                    <span v-for="modulId in rolle.modulIds" :key="modulId">
                                        {{ getFormattedModul(modulId) }}
                                        <br />
                                    </span>
                                </template>
                                <span
                                    v-for="studiengangId in rolle.studiengangIds"
                                    :key="studiengangId"
                                >
                                    {{ getFormattedStudiengang(studiengangId) }}
                                    <br />
                                </span>
                                <span
                                    v-for="fachbereichId in rolle.fachbereichIds"
                                    :key="fachbereichId"
                                >
                                    {{ getFormattedFachbereich(fachbereichId) }}
                                    <br />
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
            <template #item.isEnabled="{ item }">
                <div class="d-flex justify-center align-center">
                    <v-checkbox-btn v-model="item.isEnabled" disabled />
                </div>
            </template>

            <template #item.actions="{ item }">
                <div class="d-flex my-2 justify-center align-center ga-2">
                    <div>
                        <v-btn
                            icon="mdi-eye-outline"
                            density="compact"
                            class=""
                            color="secondary"
                        />
                        <user-edit-modal :user-id="item.userId" />
                    </div>
                    <div>
                        <v-btn
                            icon="mdi-trash-can"
                            density="compact"
                            class=""
                            color="secondary"
                            @click="userToDelete = item"
                        />
                    </div>
                    <v-btn
                        v-if="!isUserSwitched"
                        density="compact"
                        size="small"
                        color="secondary"
                        height="70px"
                        min-width="360px"
                        :title="`Als Nutzer ${item.email.email} weitermachen`"
                        :data-test="`switch-user-${item.email.email}`"
                        prepend-icon="mdi-account-group"
                        @click="switchUser(item)"
                    >
                        Als Nutzer<br />{{ item.email.email }}<br />weitermachen
                    </v-btn>
                    <v-btn
                        v-else
                        density="compact"
                        color="primary"
                        class="flex-item d-flex align-center mr-1"
                        :title="`Nutzerwechsel beenden`"
                        @click="exitSwitchUser()"
                    >
                        <v-icon
                            icon="mdi-account"
                            class="rounded-circle bg-secondary pa-1 mr-1 cursor-pointer flex-item"
                            font-scale="1.5"
                        />
                        <v-icon
                            icon="mdi-arrow-left-bold-box-outline"
                            class="rounded-circle bg-secondary pa-1 mr-1 cursor-pointer flex-item"
                            font-scale="1.5"
                        />
                        <v-icon
                            icon="mdi-account-box-outline"
                            class="rounded-circle bg-secondary pa-1 mr-1 cursor-pointer flex-item"
                            font-scale="1.5"
                        />
                    </v-btn>
                </div>
            </template>
        </v-data-table-server>

        <v-dialog :model-value="!!userToDelete" @update:model-value="userToDelete = null">
            <v-card v-if="userToDelete">
                <v-card-title>Nutzer {{ userToDelete.vorname }} {{ userToDelete.nachname }} löschen?</v-card-title>
                <v-card-text>Sind Sie sicher?</v-card-text>
                <v-card-actions>
                    <v-btn @click="removeUser(userToDelete)">Löschen</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, ref, watch } from 'vue'
    import { useDebounceFn } from '@vueuse/core'
    import { useUserStore } from '@/js/stores/user'
    import { useFachbereichStore } from '@/js/stores/fachbereich'
    import { useStudiengangStore } from '@/js/stores/studiengang'
    import UserEditModal from '@/js/components/Benutzer/UserEditModal.vue'
    import { useModuleStore } from '@/js/stores/modul'
    import { itemsPerPageOptions, itemsPerPageText, loadingText, noItemsText } from '@/js/utils'
    import { User, SecurityUser } from '@/js/model'
    import { FetchParams } from '@/js/utils/fetch-params'

    const userStore = useUserStore()
    const fachbereichStore = useFachbereichStore()
    const studiengangStore = useStudiengangStore()
    const modulStore = useModuleStore()

    const headers = ref([
        { key: 'titel', title: 'Titel', sortable: true },
        { key: 'vorname', title: 'Vorname', sortable: true },
        { key: 'nachname', title: 'Nachname', sortable: true },
        { key: 'email.email', title: 'E-Mail', sortable: true },
        { key: 'rollen', title: 'Rollen' },
        { key: 'isEnabled', title: 'Aktiv' },
        { key: 'actions', title: 'Aktionen' }
    ])

    const isUserSwitched = computed(() => store.getters['security/isUserSwitched'])
    const isLoading = computed(
        () =>
            userStore.isLoading ||
            fachbereichStore.isLoading ||
            studiengangStore.isLoading
    )
    const isLoadingModules = computed(
        () =>
            modulStore.isLoading
    )

    const userToDelete = ref<User | null>(null)
    const page = ref<number>(1)
    const itemsPerPage = ref<number>(10)

    const search = ref<string>('')
    const searchName = ref<string>('')
    const searchRollenname = ref<string>('')
    const searchStudiengangId = ref<string>('')
    const searchIsEnabled = ref<boolean | null>(null)
    const loading = ref<boolean>(true)
    const serverResults = ref<User[]>([])
    const serverItems = computed<User[]>(() => {
        return serverResults.value
            .map((user) => userStore.getUserById(user.userId))
            .filter((u): u is User => !!u)
    })
    const totalItems = ref<number>(0)

    const removeUser = async (user: User) => {
        await userStore.removeUser(user)
        userToDelete.value = null
    }

    const loadUsers = async ({ page, itemsPerPage, sortBy }: FetchParams) => {
        const params: FetchParams = {
            page,
            itemsPerPage,
            sortBy,
            ['identifier']: searchName.value,
            ['rollenName']: searchRollenname.value,
            ['studiengangId']: searchStudiengangId.value,
            isEnabled: searchIsEnabled.value
        }

        loading.value = true
        const data = await userStore.fetchPaginatedUsers(params)

        serverResults.value = data['hydra:member']
        totalItems.value = data['hydra:totalItems'] ?? 0
        loading.value = false
    }

    const switchUser = (user: SecurityUser) => {
        store.dispatch('security/switchUser', user)
    }

    const exitSwitchUser = () => {
        store.dispatch('security/exitSwitchUser')
    }

    const getFormattedFachbereich = (id: string) => {
        const fachbereich = fachbereichStore.getFachbereichById(id)
        return fachbereich ? `${fachbereich.kurzName} - ${fachbereich.name}` : '-'
    }

    const getFormattedStudiengang = (id: string) => {
        const studiengang = studiengangStore.getStudiengangById(id)
        return studiengang ? `${studiengang.kurzName} - ${studiengang.name}` : '-'
    }

    const getFormattedModul = (id: string) => {
        const modul = modulStore.getModuleById(id)
        if (!modul) return '-'

        const studiengang = modul.studiengangId
            ? studiengangStore.getStudiengangById(modul.studiengangId)
            : undefined

        return `${modul.modulname} (${studiengang?.kurzName ?? '-'})`
    }

    watch(
        [searchName, searchRollenname, searchIsEnabled, searchStudiengangId],
        useDebounceFn(() => {
            search.value = String(Date.now())
        }, 500)
    )

    onMounted(async () => {
        const promises = [
            userStore.fetchUsers(),
            fachbereichStore.fetchFachbereiche(),
            studiengangStore.fetchStudiengaenge(),
            modulStore.fetchModules()
        ]
        await Promise.all(promises)
    })
</script>
